import { groupChartData } from '../../../utils/groupChartData';

export const METRICS = [
  { key: 'alarms_count', label: 'Alarmas', color: '#e85650', unit: '' },
  { key: 'delivered_food', label: 'Alimento', color: '#e39a4f', unit: 'kg' },
  { key: 'perdidos_count', label: 'PPA', color: '#a88cf0', unit: '' },
  { key: 'visitas_count', label: 'Visitas', color: '#6fb6e6', unit: '' },
  { key: 'alarms_by_ton', label: 'Alarmas / t', color: '#e07fb0', unit: '' },
  { key: 'sfr_value', label: 'SFR', color: '#4fd1a0', unit: '%' },
];

export const seriesEntries = (value) => Array.isArray(value)
  ? value.filter((entry) => entry?.day)
  : Object.entries(value || {}).map(([day, item]) => ({ day, value: item }));

// Both center summaries and area responses use this tree, but at different depths.
// Stop at a summary so nested cage details cannot be counted twice in an area.
export function panelEntities(data, level = 'centro') {
  const entities = [];
  const walk = (node, path) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    if (node.values_summary) {
      const key = path[path.length - 1] || node.name;
      entities.push({
        id: path.join(':') || String(node.name),
        name: node.name || (level === 'centro' ? key : key.split('-').pop()),
        node,
        values: seriesEntries(node.values_summary),
      });
      return;
    }
    Object.entries(node).forEach(([key, child]) => walk(child, [...path, key]));
  };
  walk(data?.analytic_data, []);
  return entities;
}

export function metricValue(summary, key) {
  const alias = { perdidos_count: 'perdidos_cout', visitas_count: 'visitas_cout' }[key];
  const value = summary?.snapshot?.[key] ?? summary?.[key] ?? summary?.[alias];
  if (value == null || value === '') return null;
  return Number.isFinite(Number(value)) ? Number(value) : null;
}

export function summarize(values, key) {
  const numbers = values.map(({ value }) => metricValue(value, key)).filter((value) => value !== null);
  if (!numbers.length) return null;
  const total = numbers.reduce((sum, value) => sum + value, 0);
  return key === 'sfr_value' ? total / numbers.length : total;
}

const dayTime = (day) => {
  const parts = String(day).split(/[/-]/).map(Number);
  if (parts.length !== 3) return NaN;
  const [a, b, c] = parts;
  return a > 999 ? Date.UTC(a, b - 1, c) : Date.UTC(c, b - 1, a);
};

export function dailyPeriods(entities) {
  return [...new Set(entities.flatMap((entity) => entity.values.map(({ day }) => day)))].sort((a, b) => dayTime(a) - dayTime(b));
}

export function periodLabel(day, mode = 'day') {
  if (mode === 'day') return day;
  return groupChartData([{ values_summary: [{ day, value: {} }] }], mode)[0].values_summary[0].day;
}

// Resolve periods from the same grouper as V1; daily charts always receive real dates.
export function periodDays(entities, mode) {
  const periods = new Map();
  dailyPeriods(entities).forEach((day) => {
    const label = periodLabel(day, mode);
    if (!periods.has(label)) periods.set(label, []);
    periods.get(label).push(day);
  });
  return periods;
}

export function alarmTiers(entities, days) {
  const totals = { baja: 0, media: 0, alta: 0 };
  let found = false;
  const visit = (node) => {
    if (!node || typeof node !== 'object') return;
    Object.entries(node).forEach(([key, value]) => {
      if (Object.prototype.hasOwnProperty.call(totals, key)) {
        const raw = typeof value === 'number' ? value : value?.value ?? value?.alarm;
        if (raw != null && Number.isFinite(Number(raw))) {
          totals[key] += Number(raw);
          found = true;
        }
      } else if (key !== 'tasas') visit(value);
    });
  };
  entities.forEach(({ node }) => seriesEntries(node.alarms_by_tasa || node.tasa_summary)
    .filter(({ day }) => !days || days.includes(day)).forEach(({ value }) => visit(value)));
  return found ? totals : null;
}

export const formatMetric = (value, digits = 1) => value == null || !Number.isFinite(value)
  ? '—' : value.toLocaleString('es-CL', { maximumFractionDigits: digits });
